// Server-side referral link handling (Edge-runtime safe: its one import, entry-source.js, has no
// browser or Node dependencies; the middleware imports it too).
//
// WHY THIS EXISTS
// ---------------
// `/m/<uid>`, `/m/<uid>/<tracker>`, `/anime/m/…` and `/r/<uid>` used to be CLIENT components
// that wrote the attribution keys into localStorage and then `router.push`ed to a landing page.
// That meant every referral link served a full 200 HTML copy of the landing page it pointed at,
// so Google indexed e.g. `/m/kZtmHrl7…?landing=%2Fai-generator` as a duplicate of `/ai-generator`.
// `robots.txt` disallowed `/m/`, but a disallowed URL can still be indexed from external links —
// and the disallow actively PREVENTED Google from seeing the page-level `noindex`.
//
// Now the redirect happens at the edge, before any HTML exists: the crawler gets a 301 to the
// clean canonical and there is no duplicate document to index. `robots.txt` no longer disallows
// these paths precisely so Google can fetch and follow the 301.
//
// ATTRIBUTION IS PRESERVED, NOT DROPPED
// -------------------------------------
// A 301 means no page JS runs, so localStorage can no longer be written at click time. Instead
// we stash the attribution in the `pc_ref` cookie (30 days); `hydrateReferral()` (lib/
// hydrate-referral.js, run by <ReferralHydrator> on every page) copies it into localStorage under
// the keys SignUpComponent's create_account payload reads. The cookie is not deleted after that:
// it expires on its own, and sign-up falls back to it when localStorage is empty or blocked.
//
// A CLICK IS ONE BROWSER ARRIVING THROUGH A LINK, ONCE PER LINK PER UTC DAY (7 October 2026)
// ---------------------------------------------------------------------------------------------
// Until then the header pinged /api/record-event once per browser SESSION for as long as
// localStorage held an affiliate, which counted the same visitor again every session, forever:
// about ten clicks per sign-up. Now an Aiffiliate link (/m/…, /anime/m/…) puts a `clickRef` in the
// cookie, a fresh random UUID unless the cookie already holds today's (UTC) click for the same
// affiliate and link name, and the browser reports each clickRef once (hydrate-referral.js).
// Aiffiliate keeps click_ref UNIQUE, so a repeat is a duplicate, never a second click. Bots that
// run no JavaScript never report one. The redirect carries `Cache-Control: no-store` so a
// browser does not cache the 301 and skip the edge (and the cookie) on its next click.
//
// The cookie also carries, for that report only, what the affiliate can use and nothing personal:
// when (clickedAt, clickDay), the referring site's HOST (never its full URL), and the link's own
// sub-IDs `sub1`..`sub5`. The contract: promptchan-backend docs/architecture/affiliate-integration.md.
import { OWN_HOST } from './entry-source.js'

export const REFERRAL_COOKIE = 'pc_ref'

// 30 days: long enough to cover the click→signup window, short enough that a stale cookie
// can't re-attribute a visitor months later.
const REFERRAL_COOKIE_MAX_AGE = 60 * 60 * 24 * 30

/**
 * Validate and split a `?landing=` value into a path and its own query string.
 *
 * A landing value is attacker-controllable, so it may only ever be a same-origin path.
 * `//evil.com` and `/\evil.com` are protocol-relative URLs that browsers treat as absolute —
 * accepting them would have turned every referral link into an open redirect. (The old client
 * implementation passed this straight to router.push, so this also closes that hole.)
 *
 * The query is split off deliberately. `?landing=%2Fai-generator%3Futm%3Dx` is a legitimate
 * affiliate link, and the old client `router.push('/ai-generator?utm=x')` handled it. Assigning
 * the whole string to `url.pathname` instead percent-encodes the `?` into the path
 * (`/ai-generator%3Futm=x`) and 404s — so the two parts have to be applied to the two parts of
 * the URL. Returns `null` for anything not safely same-origin.
 */
function safeLanding(value) {
  if (!value || typeof value !== 'string') return null
  if (!value.startsWith('/')) return null
  if (value.startsWith('//') || value.startsWith('/\\')) return null

  // Strip any fragment first — a fragment is client-only and must never reach the pathname.
  const withoutHash = value.split('#')[0]
  const queryIndex = withoutHash.indexOf('?')
  const path =
    queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex)
  const query = queryIndex === -1 ? '' : withoutHash.slice(queryIndex + 1)

  // A value of exactly "?..." would leave an empty path.
  if (!path.startsWith('/')) return null
  return { path, query }
}

// Advertiser landing prefixes → the clean page they point at. Redirect chains are collapsed
// here: `/a/anime/<x>` used to land on `/anime`, which itself 301s to `/ai-hentai`, so the old
// flow burned two hops; likewise girl-generator → `/` and video → `/ai-porn-video-generator`.
const ADVERTISER_DESTINATIONS = {
  'ai-generator': '/ai-generator',
  'ai-porn': '/ai-porn',
  anime: '/ai-hentai',
  create: '/generate',
  'girl-generator': '/',
  'nsfw-ai-chat': '/nsfw-ai-chat',
  'nsfw-ai': '/nsfw-ai',
  video: '/ai-porn-video-generator',
}

// `?landing=` values the Aiffiliate dashboard offered that never existed as pages here, mapped
// to the closest live landing. Affiliates have already pointed domains and links at them, so
// they must resolve rather than 404 — `/ai-character` went to the /[lang] dynamic segment,
// whose layout calls notFound() for anything that isn't a supported locale.
//
// Exported so next.config.mjs can register the same pairs as top-level redirects: this map
// covers `/m/<uid>?landing=/ai-character` (collapsed to a SINGLE hop, matching how
// ADVERTISER_DESTINATIONS collapses its chains), next.config covers a direct visit to
// promptchan.com/ai-character. One source of truth so the two can't drift.
export const LEGACY_LANDING_DESTINATIONS = {
  '/ai-character': '/nsfw-ai-chat',
  '/furry-ai': '/ai-hentai',
}

// Ad-network click params the old /a/ pages captured client-side via useJuicyAdsCheck /
// useClickAduCheck. Captured here instead so a 301 doesn't lose them. Keys are the exact
// localStorage names SignUpComponent reads when it builds the create_account payload.
const AD_PARAMS = {
  track: 'dynamicS2S',
  subid: 'click_subid',
  zoneid: 'click_zoneid',
  campaignid: 'click_campaignid',
  geo: 'click_geo',
}

function collectAdParams(searchParams, into) {
  if (!searchParams) return into
  for (const [param, storageKey] of Object.entries(AD_PARAMS)) {
    const value = searchParams.get(param)
    if (value) into[storageKey] = value
  }
  return into
}

// An Aiffiliate link's own sub-IDs (`/m/<uid>?sub1=summer&sub2=tiktok`): the affiliate's labels
// for where they placed the link, reported with the click and not passed on to the landing page.
const SUB_IDS = ['sub1', 'sub2', 'sub3', 'sub4', 'sub5']
const SUB_ID_MAX = 100
// What Python's str.isprintable() accepts, so Aiffiliate never refuses a value the site sent: no
// control, format, surrogate, private-use or unassigned characters, and no separator but the space.
const PRINTABLE = /^(?:[^\p{C}\p{Z}]| )*$/u
const CLICK_REF =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const CLICKED_AT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/
const HOST = /^[a-z0-9-]+(\.[a-z0-9-]+)*$/

/** Whether a value is a click reference as the edge mints them (crypto.randomUUID). */
export const isClickRef = (value) =>
  typeof value === 'string' && CLICK_REF.test(value)

/** A sub-ID as reported: trimmed, at most 100 characters, printable; null otherwise. */
export function cleanSubId(value) {
  if (typeof value !== 'string') return null
  // Counted in code points, as Python counts them, and never cut through an emoji.
  const clean = Array.from(value.trim()).slice(0, SUB_ID_MAX).join('').trim()
  return clean && PRINTABLE.test(clean) ? clean : null
}

/**
 * The referring site's host from a `Referer` header: lower case, host only (never the path or
 * query, which can carry anything). Null when absent, unreadable, or one of our own hosts (a
 * link followed from inside the site says nothing about where the affiliate placed it).
 */
export function referrerHost(referer, ownHost) {
  let host
  try {
    host = new URL(referer).hostname.toLowerCase()
  } catch {
    return null
  }
  if (!host || OWN_HOST.test(host) || host === ownHost) return null
  return host.length <= 253 && HOST.test(host) ? host : null
}

// Today's click on the same link (same affiliate, same link name, same UTC day) keeps its
// reference and time: one click, however many times the browser follows that link today.
function sameClick(previous, attribution, day) {
  return Boolean(
    previous &&
    previous.aiffiliateUid === attribution.aiffiliateUid &&
    (previous.aiffiliateTracker ?? null) ===
      (attribution.aiffiliateTracker ?? null) &&
    previous.clickDay === day &&
    isClickRef(previous.clickRef) &&
    typeof previous.clickedAt === 'string' &&
    CLICKED_AT.test(previous.clickedAt) &&
    previous.clickedAt.startsWith(day),
  )
}

function addClick(attribution, searchParams, click) {
  const {
    referer,
    host,
    previous,
    now = Date.now(),
    newClickRef = () => crypto.randomUUID(),
  } = click
  const day = new Date(now).toISOString().slice(0, 10)
  const reuse = sameClick(previous, attribution, day)
  attribution.clickRef = reuse ? previous.clickRef : newClickRef()
  attribution.clickDay = day
  attribution.clickedAt = reuse
    ? previous.clickedAt
    : new Date(now).toISOString()
  const fromHost = referrerHost(referer, host)
  if (fromHost) attribution.referrerHost = fromHost
  for (const key of SUB_IDS) {
    const value = cleanSubId(searchParams?.get(key))
    if (value) attribution[key] = value
  }
  return attribution
}

/**
 * Match a referral URL and describe how to handle it.
 *
 * Returns `null` for non-referral paths. Otherwise
 * `{ destination, destinationQuery, attribution, consumedParams }`:
 *   - `destination`      the clean path to redirect to (never carries a query)
 *   - `destinationQuery` a query string the `?landing=` value asked for, applied by the caller
 *                        to the URL's search rather than its pathname
 *   - `attribution`      what goes in the `pc_ref` cookie: the localStorage key names the old
 *                        client pages wrote (`aiffiliateUid` for Aiffiliate links, one-f
 *                        `affiliateUid` for Promptchan's own /r/ links, the ad-network keys for
 *                        /a/), plus, on an Aiffiliate link, the click (`clickRef`, `clickDay`,
 *                        `clickedAt`, `referrerHost`, `sub1`..`sub5`)
 *   - `consumedParams`   query keys captured into the cookie, so the caller leaves them off the
 *                        destination (every other marketing param travels on)
 *
 * `click` describes the request, for an Aiffiliate link's click: `{ referer, host, previous,
 * now, newClickRef }`, where `previous` is the request's own `pc_ref` (parseReferralCookie) and
 * `host` the request's host name (a link followed from our own pages has no referring site).
 */
export function matchReferral(pathname, searchParams, click = {}) {
  const landing = safeLanding(searchParams?.get('landing') ?? null)
  // What gets STORED for attribution is the full original landing value (path + its query),
  // byte-for-byte what the old client wrote to localStorage. Only the REDIRECT is split.
  const landingValue = landing
    ? landing.query
      ? `${landing.path}?${landing.query}`
      : landing.path
    : null

  // Trailing slashes are stripped so `/m/abc/` behaves like `/m/abc`.
  const segments = pathname.replace(/\/+$/, '').split('/').filter(Boolean)

  // /a/<advertiser> and /a/<page>/<advertiser> → the clean landing page.
  //
  // These were the SAME trap `/m/` was in: client components that stashed the advertiser in
  // localStorage and then router.push'ed, so every ad link served a full 200 HTML copy of the
  // page it pointed at. They carried `noindex` via app/a/layout.jsx, but `robots.txt`
  // disallowed `/a/` — and a disallowed URL can still be indexed from external links while
  // Google is forbidden from fetching it to discover the noindex. Ad links are exactly the
  // kind of URL that gets scraped and linked, so the disallow was working against itself.
  if (segments[0] === 'a' && segments[1]) {
    // 2 segments = /a/<advertiser> (bare, → home). 3 = /a/<page>/<advertiser>.
    const named = segments.length > 2 ? segments[1] : null
    const advertiser = named ? segments[2] : segments[1]
    // An unrecognised page prefix falls back to home rather than 404ing a live ad link.
    const destination = named ? (ADVERTISER_DESTINATIONS[named] ?? '/') : '/'
    return {
      destination,
      attribution: collectAdParams(searchParams, { advertiser }),
      // The ad-network params stay on the URL as they always have; only `landing` is ours.
      consumedParams: ['landing'],
    }
  }

  // /m/<uid> and /m/<uid>/<tracker> → the ?landing= target, else /ai-porn.
  if (segments[0] === 'm' && segments[1]) {
    const attribution = { aiffiliateUid: segments[1] }
    if (segments[2]) attribution.aiffiliateTracker = segments[2]
    // Attribution records the landing value the affiliate actually chose, byte-for-byte —
    // aliasing a dead slug below must not silently rewrite what the backend receives.
    if (landingValue) attribution.landing = landingValue
    return {
      destination: landing
        ? (LEGACY_LANDING_DESTINATIONS[landing.path] ?? landing.path)
        : '/ai-porn',
      destinationQuery: landing ? landing.query : '',
      attribution: addClick(attribution, searchParams, click),
      consumedParams: ['landing', ...SUB_IDS],
    }
  }

  // /anime/m/<uid> and /anime/m/<uid>/<tracker> → always /nsfw-ai.
  // Both forms store `aiffiliateUid` (two f), like /m/. Until 7 October 2026 the bare form stored
  // one-f `affiliateUid`, copied verbatim from the client page it replaced, on the belief that
  // the backend told the two apart. It never could: sign-up sends `aiffiliateUid ||
  // affiliateUid` as one field. What the one-f key did do was (a) never count a click, because
  // the click report read only `aiffiliateUid`, and (b) leave the affiliate's slot empty, so a
  // later click on any other affiliate's link took the sign-up from the one who brought them.
  if (segments[0] === 'anime' && segments[1] === 'm' && segments[2]) {
    const attribution = { aiffiliateUid: segments[2] }
    if (segments[3]) attribution.aiffiliateTracker = segments[3]
    return {
      destination: '/nsfw-ai',
      attribution: addClick(attribution, searchParams, click),
      consumedParams: ['landing', ...SUB_IDS],
    }
  }

  // /r/<uid> → /ai-porn. A Promptchan user's own referral link, not Aiffiliate's: one-f
  // `affiliateUid`, and no click to count.
  if (segments[0] === 'r' && segments[1]) {
    return {
      destination: '/ai-porn',
      attribution: { affiliateUid: segments[1] },
      consumedParams: ['landing'],
    }
  }

  return null
}

// A browser keeps a cookie of at most 4,096 bytes (name and value) and silently drops a larger
// one, which would lose the whole attribution. Five 100-character sub-IDs in a non-Latin script
// percent-encode to more than that, so the least important parts go first until it fits.
const COOKIE_BUDGET = 3800
const DROPPED_FIRST = [...SUB_IDS].reverse().concat(['referrerHost', 'landing'])

/**
 * Serialize the attribution map for the `pc_ref` cookie.
 *
 * Deliberately plain JSON with NO encodeURIComponent: `NextResponse.cookies.set` already
 * percent-encodes the value when it serializes the Set-Cookie header. Encoding here too
 * produced a double-encoded cookie (`%257B…`), which `hydrateReferral`'s single decode left
 * as `%7B…` — not valid JSON, so every referral would have been silently dropped on parse.
 * One encode here, one decode there. (The budget is measured encoded, as it will be sent.)
 */
export function encodeReferralCookie(attribution) {
  const value = { ...attribution }
  for (const key of DROPPED_FIRST) {
    if (encodeURIComponent(JSON.stringify(value)).length <= COOKIE_BUDGET) break
    delete value[key]
  }
  return JSON.stringify(value)
}

/**
 * The attribution in a `pc_ref` value, or null. Takes the value decoded (Next's request cookies,
 * js-cookie) or as the browser stores it (document.cookie: percent-encoded). The cookie is
 * client-editable, so callers still check every value they use.
 */
export function parseReferralCookie(raw) {
  if (!raw || typeof raw !== 'string') return null
  try {
    const value = JSON.parse(
      raw.startsWith('{') ? raw : decodeURIComponent(raw),
    )
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value
      : null
  } catch {
    return null
  }
}

export const referralCookieOptions = {
  path: '/',
  // Readable by the client hydrator, so it cannot be httpOnly.
  httpOnly: false,
  sameSite: 'lax',
  // HTTPS-only in production so the attribution cookie can't be set or read over a plaintext
  // connection. Omitted in development, where the dev server is plain HTTP and a Secure cookie
  // would simply never be stored.
  secure: process.env.NODE_ENV === 'production',
  maxAge: REFERRAL_COOKIE_MAX_AGE,
}
